import type { PendingAcceptanceItem } from "@iec-id/shared/contracts/legal";
import { Button, EmptyState, Icon, IconTile, LinkButton, Markdown, Spinner } from "@iec-id/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useConsentT } from "@/components/consent/consent-i18n";
import { AuthCard, AuthHeading, AuthPending, OfflineNotice } from "@/components/signin/auth-card";
import { SwitchAccountFooter } from "@/components/signin/switch-account";
import { fallbackDestination, parseAuthSearch } from "@/lib/auth-flow";
import { useDocumentTitle } from "@/lib/document-title";
import { formatDate } from "@/lib/format-date";
import { clearHawkUser, setHawkUser } from "@/lib/hawk";
import { useLocale } from "@/lib/locale";
import { api } from "@/lib/trpc";

export const Route = createFileRoute("/consent")({
	validateSearch: parseAuthSearch,
	component: ConsentScreen,
});

/**
 * До-принятие новой редакции документа (макет 3f).
 *
 * Экран появляется между входом и сервисом, когда Legal Center выкатил
 * существенное изменение обязательного документа. Принятие фиксируется вместе
 * с хешем показанного текста: если редакция сменится, пока человек читает,
 * сервер отклонит принятие — принятым не должен оказаться другой текст.
 *
 * Возврат — по `return_to` (его ставит `authorize`, декрет Д10): человек шёл в
 * сервис, мы его прервали, и закончить обязаны там же, где прервали.
 */
function ConsentScreen() {
	const search = Route.useSearch();
	const t = useConsentT();
	const navigate = useNavigate();
	const locale = useLocale();
	const documentTitle = useDocumentTitle();

	const [openKey, setOpenKey] = useState<string | null>(null);

	const me = api.auth.me.useQuery(undefined, { retry: false });
	const pending = api.legal.pendingAcceptance.useQuery({ locale }, { retry: false });

	const leave = () => window.location.assign(fallbackDestination(search));

	/**
	 * Цикл «согласия ↔ онбординг» разрывается здесь.
	 *
	 * Новый человек не принял ещё ничего, поэтому `blocking = true`, и
	 * `authorize` отправляет его на `/consent` ДО мастера. Но документы у нас
	 * собирает четвёртый шаг мастера, а этот экран — только до-принятие новых
	 * редакций уже зарегистрированным. Оставить человека здесь значит показать
	 * ему первый экран продукта в виде юридического текста без единого слова о
	 * том, куда он попал. Поэтому: сначала мастер (там же и документы), а
	 * `return_to` едет с ним — `localUrl` переносит весь контекст.
	 */
	const needsOnboarding = me.data?.needsOnboarding === true;
	useEffect(() => {
		if (needsOnboarding) void navigate({ to: "/onboarding", search, replace: true });
	}, [needsOnboarding, navigate, search]);

	useEffect(() => {
		if (me.data?.userId) setHawkUser(me.data.userId);
		else clearHawkUser();
	}, [me.data?.userId]);

	if (me.isPending || pending.isPending || needsOnboarding) {
		return <AuthPending label={t("state.loadingAria")} />;
	}

	if (pending.isError || !pending.data) {
		return (
			<AuthCard>
				<EmptyState
					icon={<Icon name="TriangleAlert" size={32} />}
					title={t("state.loadFailed")}
					action={
						<Button variant="second" onClick={() => void pending.refetch()}>
							{t("actions.retry")}
						</Button>
					}
				/>
				<SwitchAccountFooter search={search} />
			</AuthCard>
		);
	}

	const items = pending.data.items;

	// Принимать нечего — экран открыт по прямой ссылке или последний документ
	// только что принят. Тупика быть не должно: уводим туда, куда шли.
	if (items.length === 0) {
		return (
			<AuthCard>
				<IconTile size={48} className="bg-brand-soft">
					<Icon name="ShieldCheck" size={24} />
				</IconTile>
				<AuthHeading title={t("state.allDoneTitle")} subtitle={t("state.allDoneDescription")} />
				<Button variant="primary" fullWidth onClick={leave}>
					{t("done.continue")}
				</Button>
				<SwitchAccountFooter search={search} />
			</AuthCard>
		);
	}

	const open = items.find((item) => item.key === openKey);
	if (open) {
		return (
			<DocumentReader
				item={open}
				onBack={() => setOpenKey(null)}
				onAccepted={(allAccepted) => {
					// Принят последний обязательный документ — возвращаем немедленно,
					// а не показываем экран «всё принято» с ещё одной кнопкой: человек
					// шёл в сервис, и лишний клик здесь ничего ему не сообщает.
					if (allAccepted) {
						leave();
						return;
					}
					setOpenKey(null);
					void pending.refetch();
				}}
			/>
		);
	}

	// По одному документу за раз: список из трёх карточек превращает экран
	// в реестр, а решение здесь принимается по каждому документу отдельно.
	const current = items[0];
	if (!current) return null;

	return (
		<AuthCard>
			<IconTile size={48} className="bg-warning-soft text-warning">
				<Icon name="Scroll" size={24} />
			</IconTile>

			<OfflineNotice />

			<AuthHeading
				title={t("title", { title: documentTitle(current.key, current.title) })}
				subtitle={t("effective", {
					version: current.version,
					date: formatDate(current.effectiveFrom, locale),
				})}
			/>

			{current.changeSummary ? (
				<p className="t-body text-fg-secondary">{current.changeSummary}</p>
			) : null}

			<p className="t-meta text-fg-muted">
				{pending.data.blocking
					? t("blocking")
					: pending.data.deferrableUntil
						? t("deferrable", { date: formatDate(pending.data.deferrableUntil, locale) })
						: null}
			</p>

			{items.length > 1 ? (
				<p className="t-meta text-fg-muted">{t("counter", { count: items.length })}</p>
			) : null}

			<div className="flex flex-col gap-2.5">
				<Button variant="primary" fullWidth onClick={() => setOpenKey(current.key)}>
					{t("actions.read")}
				</Button>
				{/* «Позже» существует только пока отсрочка не кончилась: рисовать
				    кнопку, которая ничего не делает, хуже, чем не рисовать её. */}
				{pending.data.blocking ? null : (
					<Button variant="second" fullWidth onClick={leave}>
						{t("actions.later")}
					</Button>
				)}
			</div>

			{/* Обязательный документ — блокирующий экран: без «войти в другой
			    аккаунт» человек, попавший сюда под чужой сессией, остаётся перед
			    выбором «принять чужим именем или закрыть вкладку». */}
			<SwitchAccountFooter search={search} />
		</AuthCard>
	);
}

/**
 * Читалка редакции: тело документа и кнопка принятия под ним.
 *
 * Текст рендерится безопасным `Markdown` из кита (без HTML), а не выводится
 * сырым: человек, принимающий согласие, обязан видеть текст, а не разметку
 * («## Заголовок», «**жирный**») — информированность согласия это требование
 * GDPR ст. 7, а не вопрос вкуса (аудит 2026-08-20, К3).
 */
function DocumentReader({
	item,
	onBack,
	onAccepted,
}: {
	item: PendingAcceptanceItem;
	onBack: () => void;
	onAccepted: (allRequiredAccepted: boolean) => void;
}) {
	const t = useConsentT();
	const locale = useLocale();
	const documentTitle = useDocumentTitle();
	const utils = api.useUtils();
	const [failure, setFailure] = useState<string | null>(null);

	const edition = api.legal.document.useQuery(
		{ key: item.key, locale, version: item.version },
		{ retry: false },
	);

	const accept = api.consents.accept.useMutation({
		onSuccess: (result) => {
			void utils.legal.pendingAcceptance.invalidate();
			void utils.consents.list.invalidate();
			onAccepted(result.allRequiredAccepted);
		},
		onError: () => setFailure(t("state.acceptFailed")),
	});

	return (
		<AuthCard className="max-w-[560px]">
			<LinkButton className="gap-2.5 text-fg-muted" onClick={onBack}>
				<Icon name="ArrowLeft" size={20} />
				{t("actions.back")}
			</LinkButton>

			<AuthHeading
				title={documentTitle(item.key, item.title)}
				subtitle={t("effective", {
					version: item.version,
					date: formatDate(item.effectiveFrom, locale),
				})}
			/>

			{/* Тексты юридических документов — только на английском (Д10): перевод
			    юридического документа это его новая редакция, а не тот же текст
			    другими словами. Говорим об этом прямо, а не подменяем язык молча. */}
			{edition.data && edition.data.resolvedLocale.slice(0, 2) !== locale.slice(0, 2) ? (
				<p className="t-meta text-fg-muted">
					{t("reader.translationFallback", { locale: edition.data.resolvedLocale })}
				</p>
			) : null}

			{edition.isPending ? (
				<div
					role="status"
					aria-live="polite"
					className="flex min-h-40 items-center justify-center gap-3 text-fg-muted"
				>
					<Spinner size={24} />
					<span className="t-meta">{t("reader.loadingAria")}</span>
				</div>
			) : edition.isError || !edition.data ? (
				<EmptyState
					icon={<Icon name="TriangleAlert" size={32} />}
					title={t("reader.loadFailed")}
					action={
						<Button variant="second" onClick={() => void edition.refetch()}>
							{t("actions.retry")}
						</Button>
					}
				/>
			) : (
				// Читалка скроллится сама, а карточка остаётся на месте: кнопка
				// принятия обязана быть видна, не досмотрев документ до конца.
				// Скроллящийся контейнер браузеры делают фокусируемым сами — свой
				// tabIndex здесь только сломал бы порядок обхода.
				<section
					className="iec-scroll max-h-[50vh] rounded-control bg-sunken p-4"
					aria-label={t("reader.aria")}
					lang={edition.data.resolvedLocale.slice(0, 2)}
				>
					<Markdown source={edition.data.bodyMarkdown} className="t-body text-fg-secondary" />
				</section>
			)}

			{failure ? (
				<p role="alert" className="t-body text-danger">
					{failure}
				</p>
			) : null}

			<Button
				variant="primary"
				fullWidth
				state={
					accept.isPending
						? "loading"
						: edition.data
							? "default"
							: // Принимать нечего, пока текст не показан: принятие фиксирует
								// хеш ровно того тела, которое человек видел.
								"disabled"
				}
				onClick={() => {
					if (!edition.data) return;
					setFailure(null);
					accept.mutate({
						documentKey: edition.data.key,
						version: edition.data.version,
						sha256: edition.data.sha256,
						source: "reconfirm",
						locale,
					});
				}}
			>
				{accept.isPending ? t("actions.accepting") : t("actions.accept", { version: item.version })}
			</Button>
		</AuthCard>
	);
}
